/**
 * components/object/EmbeddedDocumentViewer.jsx — Iter 52.
 *
 * 2d strategy: inline thumbnail in the list, expandable native PDF
 * embed in a side drawer. Images render with <img>; PDFs use <object>
 * with iframe fallback. NEVER forces a download — Phase 6 of directive.
 *
 * Field-friendly: closes with Escape, taps outside, or the close
 * button. Side drawer slides over the inspector but the drawing stays
 * visible behind everything (drawing-first).
 */
import React, { useEffect, useState } from "react";
import { X, Download, ExternalLink } from "lucide-react";
import { documentContentUrl, fetchBlobUrl } from "@/lib/documents";

export default function EmbeddedDocumentViewer({ doc, onClose }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(null);

  const isImage = (doc.content_type || "").startsWith("image/");
  const isPdf = (doc.content_type || "") === "application/pdf"
    || (doc.file_name || "").toLowerCase().endsWith(".pdf");

  useEffect(() => {
    let cancel = false;
    let blob = null;
    fetchBlobUrl(documentContentUrl(doc.id))
      .then((b) => { if (!cancel) { setSrc(b); blob = b; } })
      .catch((e) => { if (!cancel) setErr(e?.message || "Load failed"); });
    return () => {
      cancel = true;
      if (blob) URL.revokeObjectURL(blob);
    };
  }, [doc.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="embedded-document-viewer"
      data-document-id={doc.id}
    >
      <div
        className="w-[min(880px,92vw)] h-full bg-[var(--fop-surface)] border-l border-[var(--fop-border)] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-[var(--fop-border)] flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
              Document
            </div>
            <div className="text-sm font-semibold text-[var(--fop-text)] truncate">
              {doc.title}
            </div>
          </div>
          {src && (
            <a
              href={src}
              download={doc.file_name}
              className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
              data-testid="document-viewer-download"
              aria-label="Download"
              title="Download"
            >
              <Download size={14} />
            </a>
          )}
          {src && (
            <a
              href={src}
              target="_blank"
              rel="noreferrer"
              className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
              data-testid="document-viewer-open-new"
              aria-label="Open in new tab"
              title="Open in new tab"
            >
              <ExternalLink size={14} />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            data-testid="document-viewer-close"
            aria-label="Close viewer"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden bg-[var(--fop-bg)]">
          {err ? (
            <div className="p-6 text-[12px] text-[var(--st-escalated,#ef4444)]"
                  data-testid="document-viewer-error">
              {err}
            </div>
          ) : !src ? (
            <div className="p-6 text-[12px] text-[var(--fop-text-3)]"
                  data-testid="document-viewer-loading">
              Loading…
            </div>
          ) : isImage ? (
            <div className="w-full h-full flex items-center justify-center overflow-auto p-2">
              <img src={src} alt={doc.title}
                    className="max-w-full max-h-full object-contain"
                    data-testid="document-viewer-image" />
            </div>
          ) : isPdf ? (
            <object
              data={src}
              type="application/pdf"
              className="w-full h-full"
              data-testid="document-viewer-pdf"
            >
              {/* Native browser fallback if <object> can't render */}
              <iframe
                src={src}
                title={doc.title}
                className="w-full h-full border-0"
                data-testid="document-viewer-pdf-iframe"
              />
            </object>
          ) : (
            <div className="p-6 text-[12px] text-[var(--fop-text-3)]"
                  data-testid="document-viewer-unsupported">
              This document type can't be embedded. Use the open / download
              buttons above to view it externally.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
